import { useContext, useEffect, useRef, useState } from "react";

import {
  sassMutationFunctions,
  sclrMutationFunctions,
  type SASS,
} from "@/models/stff";
import { type SCLR, type MutationResponse, type LeaDataModel } from "@/models";
import { type FileSubmission } from "@/models/file-submission";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";

export const makeSassId = (s: SASS) =>
  `${s.LocalStaffId}|${s.ProgramYear}|${s.PreschoolCode}`;

export const makeSclrId = (s: SCLR) =>
  `${s.LocalStaffId}|${s.PreschoolCode}|${s.LocalClassroomId}|${s.ProgramYear}`;

export function useBlockedStaffDeletion({
  leaData,
  sassRecords,
  sclrRecords,
  blockedDeletionOpen,
}: {
  leaData: LeaDataModel;
  sassRecords: SASS[];
  sclrRecords: SCLR[];
  blockedDeletionOpen: boolean;
}) {
  const { addPendingFileSubmission, showToast, pendingFileSubmissions } =
    useContext(FileSubmissionsContext);

  const [pendingSassDeletes, setPendingSassDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingSclrDeletes, setPendingSclrDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<Set<string>>(
    new Set()
  );
  const [isSassBulkSubmitting, setIsSassBulkSubmitting] = useState(false);
  const [isSclrBulkSubmitting, setIsSclrBulkSubmitting] = useState(false);

  useEffect(() => {
    if (pendingBulkDeleteIds.size === 0) return;
    const stillPending = new Set(pendingFileSubmissions.map((fs) => fs.Id));
    const next = new Set(
      [...pendingBulkDeleteIds].filter((id) => stillPending.has(id))
    );
    if (next.size !== pendingBulkDeleteIds.size) {
      setPendingBulkDeleteIds(next);
    }
  }, [pendingFileSubmissions, pendingBulkDeleteIds]);

  useEffect(() => {
    if (!blockedDeletionOpen) return;
    const sassOrSclrIds = pendingFileSubmissions
      .filter(
        (fs) => fs.RecordTypeCode === "SASS" || fs.RecordTypeCode === "SCLR"
      )
      .map((fs) => fs.Id);
    if (sassOrSclrIds.length === 0) return;
    setPendingBulkDeleteIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of sassOrSclrIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pendingFileSubmissions, blockedDeletionOpen]);

  useEffect(() => {
    setPendingSassDeletes((prev) => {
      if (prev.size === 0) return prev;
      const stillExists = new Set(sassRecords.map(makeSassId));
      const next = new Set([...prev].filter((id) => stillExists.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sassRecords]);

  useEffect(() => {
    setPendingSclrDeletes((prev) => {
      if (prev.size === 0) return prev;
      const stillExists = new Set(sclrRecords.map(makeSclrId));
      const next = new Set([...prev].filter((id) => stillExists.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sclrRecords]);

  const sassBulkResultHandler = useRef<
    ((result: MutationResponse<SASS>) => void) | null
  >(null);
  const sclrBulkResultHandler = useRef<
    ((result: MutationResponse<SCLR>) => void) | null
  >(null);

  const sassBulkFns = sassMutationFunctions(leaData, (result) => {
    sassBulkResultHandler.current?.(result);
  });
  const sclrBulkFns = sclrMutationFunctions(leaData, (result) => {
    sclrBulkResultHandler.current?.(result);
  });

  const handleBlockedDeleteRecords = (
    groupKey: string,
    recordIds: string[]
  ) => {
    const isSass = groupKey === "sass";
    const toDeleteSass = isSass
      ? sassRecords.filter((r) => recordIds.includes(makeSassId(r)))
      : [];
    const toDeleteSclr = !isSass
      ? sclrRecords.filter((r) => recordIds.includes(makeSclrId(r)))
      : [];

    const total = isSass ? toDeleteSass.length : toDeleteSclr.length;
    if (total === 0) return;

    const recordKind = isSass ? "Staff assignment" : "Staff classroom";
    const processingMsg = `${total} ${recordKind.toLowerCase()} record${total !== 1 ? "s" : ""} submitted for deletion`;
    const successMsg = `${recordKind} record${total !== 1 ? "s" : ""} deleted successfully`;

    if (isSass) {
      setPendingSassDeletes((prev) => new Set([...prev, ...recordIds]));
      setIsSassBulkSubmitting(true);
    } else {
      setPendingSclrDeletes((prev) => new Set([...prev, ...recordIds]));
      setIsSclrBulkSubmitting(true);
    }

    let received = 0;
    const successfulSubmissions: FileSubmission[] = [];
    let anyValidationFailed = false;

    const handleResult = (
      result: MutationResponse<SASS> | MutationResponse<SCLR>
    ) => {
      received++;
      if (result.fileSubmission !== null) {
        successfulSubmissions.push(result.fileSubmission);
      } else {
        anyValidationFailed = true;
      }

      if (received === total) {
        const submittedIds = successfulSubmissions.map((s) => s.Id);
        if (submittedIds.length > 0) {
          setPendingBulkDeleteIds(
            (prev) => new Set([...prev, ...submittedIds])
          );
        }
        successfulSubmissions.forEach((sub, i) =>
          addPendingFileSubmission(sub, i === 0 ? successMsg : undefined)
        );
        if (successfulSubmissions.length > 0) {
          showToast(
            "processing",
            "Processing",
            processingMsg,
            `processing-${groupKey}`
          );
        }
        if (anyValidationFailed) {
          showToast(
            "error",
            "Error",
            `Some ${recordKind.toLowerCase()} records could not be submitted for deletion.`
          );
        }
        if (isSass) {
          sassBulkResultHandler.current = null;
          setIsSassBulkSubmitting(false);
        } else {
          sclrBulkResultHandler.current = null;
          setIsSclrBulkSubmitting(false);
        }
      }
    };

    if (isSass) {
      sassBulkResultHandler.current = handleResult;
      toDeleteSass.forEach((r) => sassBulkFns.handleSave(r, true));
    } else {
      sclrBulkResultHandler.current = handleResult;
      toDeleteSclr.forEach((r) => sclrBulkFns.handleSave(r, true));
    }
  };

  const addSassDelete = (record: SASS) => {
    setPendingSassDeletes((prev) => new Set([...prev, makeSassId(record)]));
  };

  const addSclrDelete = (record: SCLR) => {
    setPendingSclrDeletes((prev) => new Set([...prev, makeSclrId(record)]));
  };

  const reset = () => {
    setPendingSassDeletes(new Set());
    setPendingSclrDeletes(new Set());
    setPendingBulkDeleteIds(new Set());
    setIsSassBulkSubmitting(false);
    setIsSclrBulkSubmitting(false);
  };

  return {
    pendingSassDeletes,
    pendingSclrDeletes,
    addSassDelete,
    addSclrDelete,
    handleBlockedDeleteRecords,
    deletingRecords:
      isSassBulkSubmitting ||
      isSclrBulkSubmitting ||
      pendingBulkDeleteIds.size > 0,
    reset,
  };
}
